import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText, Trash2, Download, Search, Filter, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { knowledgeService } from '@/services/knowledgeService';

interface CustomDocumentsManagerProps {
  onUpdate?: () => void;
}

interface Document {
  id: string;
  title: string;
  file_name: string;
  file_type: string;
  file_size?: number;
  created_at: string;
  uploaded_by: string;
  metadata?: Record<string, any>;
}

export const CustomDocumentsManager: React.FC<CustomDocumentsManagerProps> = ({ onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dragActive, setDragActive] = useState(false);
  
  // Use Solerro tenant ID for shared content
  // In production, this should be user's actual tenant_id
  const SHARED_TENANT_ID = '73ef7154-be00-4600-a4a6-34185ea3e6b4';

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      if (user && mounted) {
        await loadDocuments();
      }
    };
    
    load();
    
    return () => {
      mounted = false;
    };
  }, [user]);

  const loadDocuments = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Prevent concurrent loading
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      // Use shared tenant ID to load documents
      const docs = await knowledgeService.getDocuments(SHARED_TENANT_ID, 'custom_docs');
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      // Don't show error toast for initial load failure - just show empty state
      setDocuments([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleFileUpload = async (files: FileList) => {
    
    if (!user?.id) {
      console.error('No user ID found');
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file
      if (!validateFile(file)) {
        continue;
      }

      try {
        const result = await knowledgeService.uploadDocument(
          file,
          file.name.replace(/\.[^/.]+$/, ""), // Remove extension for title
          'custom_docs',
          SHARED_TENANT_ID,  // Use shared tenant ID since user doesn't have tenant_id
          user.id
        );

        if (result.success) {
          results.push(`✅ ${file.name}`);
        } else {
          results.push(`❌ ${file.name}: ${result.error}`);
        }
      } catch (error) {
        console.error('Upload error for', file.name, ':', error);
        results.push(`❌ ${file.name}: Upload failed`);
      }
    }

    setUploading(false);
    
    // Show results
    toast({
      title: "Upload Complete",
      description: results.join('\n'),
      duration: 5000
    });

    // Refresh documents and parent stats
    await loadDocuments();
    onUpdate?.();
  };

  const validateFile = (file: File): boolean => {
    // File size check (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `${file.name} exceeds 10MB limit`,
        variant: "destructive"
      });
      return false;
    }

    // File type check
    const allowedTypes = [
      'text/plain',
      'text/markdown',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    const allowedExtensions = ['.txt', '.md', '.pdf', '.docx', '.doc'];
    
    const hasValidType = allowedTypes.includes(file.type);
    const hasValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidType && !hasValidExtension) {
      toast({
        title: "Unsupported file type",
        description: `${file.name} must be TXT, MD, PDF, or Word document`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [user]);

  const handleDeleteDocument = async (documentId: string, fileName: string) => {
    if (!confirm(`Are you sure you want to delete "${fileName}"?`)) return;

    try {
      await knowledgeService.deleteDocument(documentId);
      toast({
        title: "Success",
        description: "Document deleted successfully"
      });
      await loadDocuments();
      onUpdate?.();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive"
      });
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.file_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'pdf' && doc.file_type === 'application/pdf') ||
                         (filterType === 'word' && doc.file_type.includes('word')) ||
                         (filterType === 'text' && (doc.file_type.startsWith('text/') || doc.file_name.endsWith('.md')));
    
    return matchesSearch && matchesFilter;
  });

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeIcon = (fileType: string) => {
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.startsWith('text/')) return '📃';
    return '📋';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Custom Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {dragActive ? 'Drop files here' : 'Upload documents to knowledge base'}
            </h3>
            <p className="text-muted-foreground mb-4">
              Drag and drop files or click to browse
            </p>
            <div className="space-y-2">
              <input
                type="file"
                multiple
                accept=".txt,.md,.pdf,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileUpload(e.target.files);
                  }
                }}
                className="hidden"
                id="custom-doc-file-upload"
                disabled={uploading}
              />
              <Button 
                type="button"
                disabled={uploading}
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  const fileInput = document.getElementById('custom-doc-file-upload') as HTMLInputElement;
                  if (fileInput) {
                    fileInput.click();
                  } else {
                    console.error('File input not found');
                  }
                }}
              >
                {uploading ? 'Processing...' : 'Select Files'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Supported: TXT, MD, PDF, DOC, DOCX (max 10MB each)
              </p>
            </div>
          </div>

          {uploading && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-blue-700">
                  Processing documents and generating embeddings...
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Library ({filteredDocuments.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="word">Word</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Documents Grid */}
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                {documents.length === 0 ? 'No documents yet' : 'No documents found'}
              </h3>
              <p className="text-muted-foreground">
                {documents.length === 0 
                  ? 'Upload your first document to get started'
                  : 'Try adjusting your search or filter'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getFileTypeIcon(doc.file_type)}</span>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-sm truncate" title={doc.title}>
                            {doc.title}
                          </h4>
                          <p className="text-xs text-muted-foreground truncate" title={doc.file_name}>
                            {doc.file_name}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id, doc.file_name)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Size:</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Uploaded:</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Custom Document
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4" />
            Usage Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>• Documents are automatically processed and chunked for semantic search</p>
          <p>• Each upload generates vector embeddings using OpenAI's API</p>
          <p>• Large documents are split into smaller chunks for better retrieval</p>
          <p>• Only admins can upload and manage documents</p>
          <p>• Documents are tenant-isolated for security</p>
        </CardContent>
      </Card>
    </div>
  );
};